/**
 * Credit Usage Analytics Dashboard
 * Admin interface for monitoring credit consumption and usage patterns
 */

import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { Card } from '../../components/ui/Card';
import { useToast } from '../../hooks/useToast';
import { API_BASE_URL } from '../../utils/constants';

interface CreditSummary {
  total_credits_consumed: number;
  unique_users: number;
  total_ai_messages: number;
  time_period: string;
}

interface UserUsage {
  user_id: string;
  user_email: string | null;
  credits_consumed: number;
  message_count: number;
  most_used_model: string | null;
}

interface ModelStats {
  provider: string;
  model: string;
  credit_cost: number;
  usage_count: number;
  total_credits: number;
}

interface ApiKeyStatus {
  api_key_id: string;
  label: string;
  user_email: string | null;
  credit_limit: number | null;
  credits_used: number;
  credits_remaining: number | null;
  usage_percentage: number;
  reset_period: string | null;
  resets_at: string | null;
}

interface TimelineData {
  date: string;
  credits: number;
  messages: number;
}

export const CreditAnalytics: React.FC = () => {
  const { admin } = useAdminAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(7);

  const [summary, setSummary] = useState<CreditSummary | null>(null);
  const [userUsage, setUserUsage] = useState<UserUsage[]>([]);
  const [modelStats, setModelStats] = useState<ModelStats[]>([]);
  const [apiKeyStatus, setApiKeyStatus] = useState<ApiKeyStatus[]>([]);
  const [timeline, setTimeline] = useState<TimelineData[]>([]);

  // Modal state for user model details
  const [selectedUser, setSelectedUser] = useState<UserUsage | null>(null);
  const [showUserDetailsModal, setShowUserDetailsModal] = useState(false);
  const [userModels, setUserModels] = useState<Array<{
    model: string;
    message_count: number;
    total_credits: number;
    avg_cost: number;
  }>>([]);
  const [loadingModels, setLoadingModels] = useState(false);

  useEffect(() => {
    loadAnalytics();
  }, [timeRange]);

  const loadAnalytics = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const headers = {
        'Authorization': token ? `Bearer ${token}` : ''
      };

      // Load all analytics in parallel
      const [summaryRes, usersRes, modelsRes, keysRes, timelineRes] = await Promise.all([
        fetch(`${API_BASE_URL}/admin/credit-analytics/summary?days=${timeRange}`, { headers }),
        fetch(`${API_BASE_URL}/admin/credit-analytics/users?days=${timeRange}&limit=50`, { headers }),
        fetch(`${API_BASE_URL}/admin/credit-analytics/models?days=${timeRange}`, { headers }),
        fetch(`${API_BASE_URL}/admin/credit-analytics/api-keys`, { headers }),
        fetch(`${API_BASE_URL}/admin/credit-analytics/timeline?days=${timeRange}`, { headers })
      ]);

      if (summaryRes.ok) setSummary(await summaryRes.json());
      if (usersRes.ok) setUserUsage(await usersRes.json());
      if (modelsRes.ok) setModelStats(await modelsRes.json());
      if (keysRes.ok) setApiKeyStatus(await keysRes.json());
      if (timelineRes.ok) {
        const data = await timelineRes.json();
        setTimeline(data.timeline || []);
      }

    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load credit analytics',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num);
  };

  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return 'text-red-600 bg-red-50';
    if (percentage >= 80) return 'text-orange-600 bg-orange-50';
    if (percentage >= 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };

  const loadUserModels = async (userId: string) => {
    setLoadingModels(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${API_BASE_URL}/admin/credit-analytics/user/${userId}/models?days=${timeRange}`,
        {
          headers: {
            'Authorization': token ? `Bearer ${token}` : ''
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setUserModels(data.models || []);
      } else {
        setUserModels([]);
      }
    } catch (error) {
      console.error('Failed to load user models:', error);
      setUserModels([]);
    } finally {
      setLoadingModels(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Credit Usage Analytics</h1>
            <p className="text-gray-600 mt-2">Monitor credit consumption and usage patterns</p>
          </div>

          <select
            value={timeRange}
            onChange={(e) => setTimeRange(Number(e.target.value))}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value={1}>Last 24 hours</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="p-6">
            <div className="text-sm font-medium text-gray-500 mb-1">Total Credits Consumed</div>
            <div className="text-3xl font-bold text-blue-600">
              {summary ? formatNumber(summary.total_credits_consumed) : '0'}
            </div>
            <div className="text-xs text-gray-400 mt-1">{summary?.time_period}</div>
          </Card>

          <Card className="p-6">
            <div className="text-sm font-medium text-gray-500 mb-1">Total AI Messages</div>
            <div className="text-3xl font-bold text-purple-600">
              {summary ? formatNumber(summary.total_ai_messages) : '0'}
            </div>
            <div className="text-xs text-gray-400 mt-1">API calls processed</div>
          </Card>

          <Card className="p-6">
            <div className="text-sm font-medium text-gray-500 mb-1">Active Users</div>
            <div className="text-3xl font-bold text-green-600">
              {summary ? formatNumber(summary.unique_users) : '0'}
            </div>
            <div className="text-xs text-gray-400 mt-1">Unique users consuming credits</div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Top Users by Credit Usage */}
          <Card>
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Top Users by Credit Usage</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Credits</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Messages</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {userUsage.slice(0, 10).map((user, idx) => (
                    <tr key={user.user_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {user.user_email || user.user_id}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className="text-sm font-bold text-blue-600">
                          {formatNumber(user.credits_consumed)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                        {formatNumber(user.message_count)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={async () => {
                            setSelectedUser(user);
                            setShowUserDetailsModal(true);
                            await loadUserModels(user.user_id);
                          }}
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title="View model usage details"
                        >
                          <svg className="w-5 h-5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Model Usage Statistics */}
          <Card>
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Credit Usage by Model</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cost/Call</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Credits</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {modelStats.slice(0, 10).map((model, idx) => (
                    <tr key={`${model.provider}-${model.model}`} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{model.model}</div>
                        <div className="text-xs text-gray-500">{model.provider}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                        {model.credit_cost} credits
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className="text-sm font-bold text-purple-600">
                          {formatNumber(model.total_credits)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {/* API Keys with Credit Limits */}
        <Card>
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">API Keys Credit Status</h2>
            <p className="text-sm text-gray-500 mt-1">Monitoring keys with credit limits</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Label</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Used/Limit</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Usage %</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reset</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {apiKeyStatus.map((key) => (
                  <tr key={key.api_key_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {key.label}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {key.user_email || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <span className="font-medium">{formatNumber(key.credits_used)}</span>
                      <span className="text-gray-400"> / {formatNumber(key.credit_limit || 0)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getUsageColor(key.usage_percentage)}`}>
                        {key.usage_percentage.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {key.reset_period || 'Never'}
                      {key.resets_at && (
                        <div className="text-xs text-gray-400">
                          {new Date(key.resets_at).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {apiKeyStatus.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                No API keys with credit limits configured
              </div>
            )}
          </div>
        </Card>

        {/* Timeline Chart (Simple Text Version) */}
        {timeline.length > 0 && (
          <Card className="mt-8">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Credit Usage Timeline</h2>
            </div>
            <div className="p-6">
              <div className="space-y-2">
                {timeline.map((day, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-100">
                    <div className="text-sm font-medium text-gray-700">
                      {new Date(day.date).toLocaleDateString()}
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-sm">
                        <span className="font-semibold text-blue-600">{formatNumber(day.credits)}</span>
                        <span className="text-gray-500 ml-1">credits</span>
                      </div>
                      <div className="text-sm">
                        <span className="font-semibold text-purple-600">{formatNumber(day.messages)}</span>
                        <span className="text-gray-500 ml-1">messages</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* User Details Modal */}
        {showUserDetailsModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto m-4">
              {/* Modal Header */}
              <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">User Credit Details</h3>
                  <p className="text-sm text-gray-500 mt-1">{selectedUser.user_email || selectedUser.user_id}</p>
                </div>
                <button
                  onClick={() => setShowUserDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                {/* Summary Stats */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-600 mb-1">Total Credits</div>
                    <div className="text-2xl font-bold text-blue-600">
                      {formatNumber(selectedUser.credits_consumed)}
                    </div>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-600 mb-1">Total Messages</div>
                    <div className="text-2xl font-bold text-purple-600">
                      {formatNumber(selectedUser.message_count)}
                    </div>
                  </div>
                </div>

                {/* Model Usage - Show all models */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Model Usage Breakdown</h4>

                  {loadingModels ? (
                    <div className="text-center py-8 text-gray-500">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                      Loading model data...
                    </div>
                  ) : userModels.length > 0 ? (
                    <div className="space-y-3">
                      {userModels.map((model, idx) => (
                        <div key={idx} className="bg-gray-50 p-4 rounded-lg hover:bg-gray-100 transition-colors">
                          <div className="flex justify-between items-center">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{model.model}</div>
                              <div className="text-xs text-gray-500">
                                Avg: {model.avg_cost.toFixed(2)} credits/call
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-semibold text-blue-600">
                                {formatNumber(model.total_credits)} credits
                              </div>
                              <div className="text-xs text-gray-500">
                                {formatNumber(model.message_count)} messages
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-gray-50 p-6 rounded-lg text-center text-gray-500">
                      <svg className="w-12 h-12 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                      </svg>
                      <p className="text-sm">No model usage data available</p>
                      <p className="text-xs mt-1">Model information may not be stored for older messages</p>
                    </div>
                  )}
                </div>

                {/* Average Cost */}
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">Average Cost per Message</div>
                  <div className="text-xl font-bold text-gray-900">
                    {selectedUser.message_count > 0
                      ? (selectedUser.credits_consumed / selectedUser.message_count).toFixed(2)
                      : '0'
                    } credits/message
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-6 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={() => setShowUserDetailsModal(false)}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
